import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'confirm' | 'loading' | 'success' | 'error'>('confirm');

  const handleUnsubscribe = async () => {
    if (!token) return;
    setStatus('loading');
    try {
      const { data, error } = await supabase.functions.invoke('unsubscribe', {
        body: { token },
      });
      if (error || !data?.ok) {
        throw new Error(
          data?.error?.message ||
            (error instanceof Error ? error.message : 'Unsubscribe failed'),
        );
      }
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="font-serif text-xl">Unsubscribe</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {!token ? (
            <div className="space-y-2">
              <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">Invalid unsubscribe link.</p>
            </div>
          ) : status === 'confirm' ? (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to unsubscribe?
              </p>
              <p className="text-xs text-muted-foreground">
                You won't receive future emails from us.
              </p>
              <Button onClick={handleUnsubscribe} variant="destructive" className="w-full">
                Yes, Unsubscribe
              </Button>
            </>
          ) : status === 'loading' ? (
            <div className="py-4">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : status === 'success' ? (
            <div className="space-y-2">
              <CheckCircle className="h-8 w-8 text-primary mx-auto" />
              <p className="text-sm text-foreground font-medium">You've been unsubscribed.</p>
              <p className="text-xs text-muted-foreground">
                You've been unsubscribed from all future emails and automatic event registrations.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
              <p className="text-sm text-muted-foreground">Something went wrong. Please try again.</p>
              <Button onClick={handleUnsubscribe} variant="outline" size="sm">
                Retry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
